import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ModalDialog } from 'components/ModalDialog';
import type { ChapterEnum, PriceListUerRowData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { CreateOrEditUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer/CreateOrEditUer';
import Styles from 'pages/Project/UerProject/WorkOrder/WorkOrderAddUERTable/WorkOrderAddUer.module.css';
import { Button, cnMixSpace, HeaderSearchBar, IconClose } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { useUerWorkOrderPriceListColumns } from '../../Uer/AddUerToWorkOrder/hooks/useUerWorkOrderPriceListColumns';
import { useUerWorkOrderPriceListFilters } from '../../Uer/AddUerToWorkOrder/hooks/useUerWorkOrderPriceListFilters';
import { useAddItemsToWorkOrderChange } from '../hooks';
import { addUerToWorkOrderChange } from '../WorkOrderChangeQueries';

import { useUerWorkOrderChangePriceListData } from './hooks/useUerWorkOrderChangePriceListData';
import { useUerWorkOrderChangePriceListFilters } from './hooks/useUerWorkOrderChangePriceListFilters';

interface IAddUerToWorkOrderChangeTable {
    chapter: ChapterEnum;
    workOrderChangeId: number;
    workOrderObjectId: number;
    workOrderChangeObjectId?: number;
    handleClose: () => void;
    constructionTypeId: number;
    refreshTable: () => void;
    contractId: number;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    catalogUerBaseName: string;
    catalogVersion: string;
}

export const AddUerToWorkOrderChangeTable: React.FC<IAddUerToWorkOrderChangeTable> = (props) => {
    const {
        workOrderChangeObjectId,
        chapter,
        handleClose,
        constructionTypeId,
        refreshTable,
        contractId,
        workOrderChangeUerRules,
        workOrderChangeId,
        workOrderObjectId,
        catalogUerBaseName,
        catalogVersion,
    } = props;

    const tableApiRef = useRef<AgGridReact<PriceListUerRowData> | null>(null);

    const { where, onSearchStringClear, onSearchStringChange, searchString, onSearch, changeFilterHandler } =
        useUerWorkOrderPriceListFilters();

    const { uerWorkOrderChangePriceListDataSource, allIds } = useUerWorkOrderChangePriceListData({
        workOrderObjectId,
        workOrderChangeId,
        workOrderChangeObjectId,
        chapter,
        where,
    });

    const columnsFilters = useUerWorkOrderChangePriceListFilters({
        workOrderChangeObjectId,
        chapter,
        changeFilterHandler,
    });

    const { columns, uerInputs, checkedIds, setCheckedIds } = useUerWorkOrderPriceListColumns({
        constructionTypeId,
        allIds,
        columnsFilters,
    });

    const variables = useMemo(
        () => ({
            workOrderChangeObjectId,
            chapter,
            uerInputs,
            workOrderChangeId,
            workOrderObjectId,
        }),
        [chapter, uerInputs, workOrderChangeId, workOrderChangeObjectId, workOrderObjectId],
    );

    const onSuccessAddHandler = useCallback(() => {
        refreshTable();
        handleClose();
    }, [handleClose, refreshTable]);

    const onAddOrEditSuccess = useCallback(() => {
        tableApiRef.current?.api?.refreshInfiniteCache?.();
    }, []);

    const {
        addToWorkOrderChangeHandler,
        addResponse,
        addStatus,
        addErr,
        addIsSubmitted,
        closeErrorModal,
        handleCreateReport,
    } = useAddItemsToWorkOrderChange({
        query: addUerToWorkOrderChange,
        requestId: 'uerAddUerToWorkOrderChange',
        variables,
        onSuccessAddHandler,
    });

    const isConfirmBtnDisabled = useMemo(
        () => !Object.keys(checkedIds).length || !workOrderChangeUerRules.canAddingEntries,
        [checkedIds, workOrderChangeUerRules.canAddingEntries],
    );

    return (
        <ModalDialog
            isOpen
            title="Добавление расценки из прейскуранта расценок"
            onClose={handleClose}
            onConfirm={addToWorkOrderChangeHandler}
            btnConfirmLabel="Добавить в Изм. к НЗ"
            btnConfirmDisabled={isConfirmBtnDisabled}
            withTitleCloseButton
            className={Styles.modal}
            style={{ maxWidth: '1280px' }}
            leftPanel={
                <CreateOrEditUer
                    constructionTypeId={constructionTypeId}
                    onSave={onAddOrEditSuccess}
                    contractId={contractId}
                    closeModal={handleClose}
                    chapter={chapter}
                    setCheckedIds={setCheckedIds}
                    accessRules={workOrderChangeUerRules}
                    fromWorkOrder
                    catalogName={catalogUerBaseName}
                    catalogVersion={catalogVersion}
                />
            }
        >
            <div className={Styles.modalBody}>
                <div className={cnMixSpace({ mT: 'xl', mB: 'm' })} style={{ position: 'relative' }}>
                    <HeaderSearchBar
                        placeholder="Поиск расценки в прейскуранте"
                        label="поиск"
                        value={searchString}
                        onChange={onSearchStringChange}
                        onSearch={onSearch}
                        style={{ width: '100%', maxWidth: 'none' }}
                    />
                    <Button
                        label="Очистить"
                        view="ghost"
                        iconLeft={IconClose}
                        form="round"
                        size="xs"
                        onlyIcon
                        onClick={onSearchStringClear}
                        className="clearSearchStringBtn"
                    />
                </div>
                <div className={`${Styles.tableBody} ag-theme-alpine WorkOrderChangePrice`}>
                    <AgGridTable
                        datasource={uerWorkOrderChangePriceListDataSource}
                        columnDefs={columns}
                        getRowId={getRowId}
                        rowModelType="infinite"
                        maxConcurrentDatasourceRequests={1}
                        maxBlocksInCache={50}
                        cacheBlockSize={50}
                        ref={tableApiRef}
                        headerHeight={70}
                    />
                </div>

                <ErrorHandlingModal
                    titleText="Добавление расценки из прейскуранта расценок"
                    isSubmitted={addIsSubmitted}
                    statusRequest={addStatus}
                    dataRequest={addResponse?.uerAddUerToWorkOrderChange}
                    errorsRequest={addErr}
                    handleCloseModalError={closeErrorModal}
                    handleCreateReport={handleCreateReport}
                />
            </div>
        </ModalDialog>
    );
};

AddUerToWorkOrderChangeTable.displayName = 'AddUerToWorkOrderChangeTable';
